/** SUNTRONIX ONE — Apple Services (Automated Device Enrollment + AppleCare+). */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { AppleMonthlyReports } from "./AppleMonthlyReports";
import IngramMicroTab from "@/components/operations/IngramMicroTab";
import RedingtonHistoryTab from "@/components/operations/RedingtonHistoryTab";
import DistributorEnrollmentTab from "@/components/operations/DistributorEnrollmentTab";
import EnrollmentConfirmationsPanel from "@/components/operations/EnrollmentConfirmationsPanel";
import AppleCareHistoryTab from "@/components/operations/AppleCareHistoryTab";
import AppleCareIntegrationCard from "@/components/operations/AppleCareIntegrationCard";
import DistributorActivationPanel from "@/components/operations/DistributorActivationPanel";
import { toast } from "sonner";
import {
  Apple, ShieldCheck, ClipboardList, Users, AlertTriangle,
  CheckCircle2, Clock, PackageCheck, Mail, Settings,
  Plus, Upload, Search, Plug, RefreshCw, XCircle, HelpCircle, Loader2,
  LogIn, Save, X, ScanSearch, MonitorSmartphone, Boxes, Send, History,
} from "lucide-react";

const TABS = [
  { id: "overview",    label: "Overview",                icon: Apple },
  { id: "profiles",    label: "Customer Apple Profiles", icon: Users },
  { id: "enrollments", label: "Device Enrollment (DEP)", icon: ClipboardList },
  { id: "applecare",   label: "AppleCare+",              icon: ShieldCheck },
  { id: "daily",       label: "Daily Completions",       icon: CheckCircle2 },
  { id: "monthly",     label: "Monthly Reports",         icon: PackageCheck },
  { id: "exceptions",  label: "Exceptions",              icon: AlertTriangle },
  { id: "comms",       label: "Communications",          icon: Mail },
  { id: "redington",   label: "Redington Connection",    icon: Plug },
  { id: "history",     label: "Enrollment History",      icon: ScanSearch },
  { id: "submit",      label: "Distributor Enrollment",  icon: Send },
  { id: "achistory",   label: "AppleCare History",       icon: History },
  { id: "ingram",      label: "Ingram Micro",            icon: Boxes },
  { id: "config",      label: "Configuration",           icon: Settings },
];

const ENROLL_BADGE = {
  PENDING:         "bg-amber-50 text-amber-700 ring-amber-200",
  PROCESSING:      "bg-blue-50 text-blue-700 ring-blue-200",
  COMPLETED:       "bg-emerald-50 text-emerald-700 ring-emerald-200",
  FAILED:          "bg-rose-50 text-rose-700 ring-rose-200",
  REVIEW_REQUIRED: "bg-orange-50 text-orange-700 ring-orange-200",
  CANCELLED:       "bg-slate-100 text-slate-500 ring-slate-200",
};
const CARE_BADGE = {
  REQUESTED:         "bg-amber-50 text-amber-700 ring-amber-200",
  PROCESSING:        "bg-blue-50 text-blue-700 ring-blue-200",
  ACTIVATED:         "bg-emerald-50 text-emerald-700 ring-emerald-200",
  ACTIVATION_FAILED: "bg-rose-50 text-rose-700 ring-rose-200",
  REVIEW_REQUIRED:   "bg-orange-50 text-orange-700 ring-orange-200",
  EXPIRED:           "bg-slate-100 text-slate-500 ring-slate-200",
  CANCELLED:         "bg-slate-100 text-slate-500 ring-slate-200",
};

const badge = (map, s) =>
  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] ring-1 ${map[s] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>{s}</span>;


export default function AppleServices() {
  const [tab, setTab]                 = useState(() => new URLSearchParams(window.location.search).get("tab") || "overview");
  const [meta, setMeta]               = useState(null);
  const [overview, setOverview]       = useState(null);
  const [profiles, setProfiles]       = useState([]);
  const [enrollments, setEnrollments] = useState([]);
  const [applecare, setApplecare]     = useState([]);
  const [busy, setBusy]               = useState(false);

  const refreshAll = useCallback(async () => {
    try {
      const [m, o, p, e, c] = await Promise.all([
        api.get("/apple-services/meta"),
        api.get("/apple-services/overview"),
        api.get("/apple-services/profiles"),
        api.get("/apple-services/enrollments"),
        api.get("/apple-services/applecare"),
      ]);
      setMeta(m.data); setOverview(o.data);
      setProfiles(p.data.items || []);
      setEnrollments(e.data.items || []);
      setApplecare(c.data.items || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load Apple Services");
    }
  }, []);

  useEffect(() => { refreshAll(); }, [refreshAll]);

  const seedDemo = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/apple-services/seed-demo");
      toast.success(`Seeded ${data.profiles.length} profiles, ${data.enrollments.length} enrollments, ${data.applecare.length} AppleCare`);
      await refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Seed failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-[1600px] mx-auto space-y-4" data-testid="apple-services">
      <header className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-slate-900 text-white grid place-items-center">
            <Apple className="h-5.5 w-5.5" />
          </div>
          <div>
            <div className="text-xl font-heading font-semibold" data-testid="apple-svc-title">
              Apple Services
            </div>
            <div className="text-[11px] text-slate-500">
              Automated Device Enrollment (DEP) · AppleCare+ for Business ·
              Customer-safe monthly reporting
            </div>
          </div>
        </div>
        {meta?.current_user?.can_admin ? (
          <button onClick={seedDemo} disabled={busy}
              data-testid="apple-svc-seed-demo"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
            {busy ? "Seeding…" : "Seed Demo Data"}
          </button>
        ) : null}
      </header>

      {/* Tabs */}
      <nav className="flex flex-wrap gap-1 border-b border-slate-200" data-testid="apple-svc-tabs">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
                data-testid={`apple-svc-tab-${t.id}`}
                className={"flex items-center gap-1.5 text-[11.5px] px-3 py-2 border-b-2 -mb-px " +
                  (active
                    ? "border-slate-900 text-slate-900 font-semibold"
                    : "border-transparent text-slate-500 hover:text-slate-700")}>
              <Icon className="h-3.5 w-3.5" /> {t.label}
            </button>
          );
        })}
      </nav>

      {tab === "overview"    && <OverviewTab overview={overview} />}
      {tab === "profiles"    && <ProfilesTab profiles={profiles} refresh={refreshAll} meta={meta} />}
      {tab === "enrollments" && <EnrollmentsTab enrollments={enrollments} profiles={profiles} refresh={refreshAll} meta={meta} />}
      {tab === "applecare"   && <AppleCareTab applecare={applecare} profiles={profiles} refresh={refreshAll} meta={meta} goTo={setTab} />}
      {tab === "daily"       && <DailyCompletionsTab meta={meta} />}
      {tab === "monthly"     && <AppleMonthlyReports />}
      {tab === "exceptions"  && <ExceptionsTab />}
      {tab === "comms"       && <><EnrollmentConfirmationsPanel isAdmin={!!meta?.current_user?.can_admin} /><CommunicationsTab profiles={profiles} /></>}
      {tab === "redington"   && <RedingtonConnectionTab meta={meta} />}
      {tab === "history"     && <RedingtonHistoryTab />}
      {tab === "submit"      && <DistributorEnrollmentTab />}
      {tab === "achistory"   && <AppleCareHistoryTab />}
      {tab === "ingram"      && <IngramMicroTab />}
      {tab === "config"      && <ConfigurationTab meta={meta} />}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Redington Connection (Stage 1 — truthful connection status only)
// ---------------------------------------------------------------------------
const RED_STATUS = {
  NOT_CONFIGURED: {
    label: "Not configured", tone: "bg-slate-100 text-slate-600 ring-slate-200",
    Icon: HelpCircle, dot: "bg-slate-400",
  },
  AUTH_REQUIRED: {
    label: "Authentication required", tone: "bg-amber-50 text-amber-700 ring-amber-200",
    Icon: AlertTriangle, dot: "bg-amber-500",
  },
  CONNECTED: {
    label: "Connected", tone: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    Icon: CheckCircle2, dot: "bg-emerald-500",
  },
  CONNECTION_FAILED: {
    label: "Connection failed", tone: "bg-rose-50 text-rose-700 ring-rose-200",
    Icon: XCircle, dot: "bg-rose-500",
  },
};

const fmtWhen = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

function RedingtonConnectionTab({ meta }) {
  const [conn, setConn] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [baseInput, setBaseInput] = useState("");
  const [savingBase, setSavingBase] = useState(false);
  const canCheck = !!meta?.current_user?.can_write;
  const canAdmin = !!meta?.current_user?.can_admin;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/apple-services/redington/connection");
      setConn(data.connection);
      setBaseInput(data.connection.base_url || "");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load Redington connection status");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const check = async () => {
    setChecking(true);
    try {
      const { data } = await api.post("/apple-services/redington/connection/check");
      setConn(data.connection);
      toast.success("Connection check completed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Connection check failed to run");
    } finally { setChecking(false); }
  };

  const saveBase = async () => {
    setSavingBase(true);
    try {
      const { data } = await api.patch("/apple-services/redington/config", { base_url: baseInput.trim() });
      setConn(data.connection);
      toast.success("Portal endpoint saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save base URL");
    } finally { setSavingBase(false); }
  };

  if (loading) {
    return (
      <div className="py-16 grid place-items-center text-slate-400" data-testid="redington-loading">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }
  if (!conn) return null;

  const s = RED_STATUS[conn.status] || RED_STATUS.NOT_CONFIGURED;
  const StatusIcon = s.Icon;
  const isConnected = conn.status === "CONNECTED";

  return (
    <div className="max-w-3xl space-y-4" data-testid="redington-connection-tab">
      {/* Status card */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="redington-status-card">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-slate-900 text-white grid place-items-center">
              <Plug className="h-5 w-5" />
            </div>
            <div>
              <div className="text-[15px] font-semibold text-slate-900">Redington DEP Connection</div>
              <div className="text-[11px] text-slate-500 mt-0.5">
                Device Enrollment Program · secure backend session (read-only)
              </div>
            </div>
          </div>
          <span data-testid="redington-status-badge"
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ring-1 ${s.tone}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
            <StatusIcon className="h-3.5 w-3.5" /> {s.label}
          </span>
        </div>

        <p className="mt-4 text-[13px] text-slate-600 leading-relaxed" data-testid="redington-status-message">
          {conn.message}
        </p>

        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="rounded-lg bg-slate-50 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
              <Clock className="h-3 w-3" /> Last connection check
            </div>
            <div className="text-[13px] text-slate-800 mt-0.5" data-testid="redington-last-check">
              {fmtWhen(conn.last_check_at)}
            </div>
          </div>
          <div className="rounded-lg bg-slate-50 px-3 py-2.5">
            <div className="text-[10px] uppercase tracking-wide text-slate-400 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" /> Last successful connection
            </div>
            <div className="text-[13px] text-slate-800 mt-0.5" data-testid="redington-last-success">
              {fmtWhen(conn.last_success_at)}
            </div>
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
          <span className={`px-2 py-0.5 rounded ring-1 ${conn.base_url_configured ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-slate-100 text-slate-500 ring-slate-200"}`}>
            Portal endpoint {conn.base_url_configured ? "configured" : "not configured"}
          </span>
          <span className={`px-2 py-0.5 rounded ring-1 ${conn.session_provisioned ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-slate-100 text-slate-500 ring-slate-200"}`}>
            Backend session {conn.session_provisioned ? "active" : "not established"}
          </span>
        </div>

        {/* Actions */}
        <div className="mt-5 flex flex-wrap items-center gap-3">
          <button onClick={check} disabled={checking || !canCheck}
            data-testid="redington-check-btn"
            className="inline-flex items-center gap-2 text-[12px] px-3.5 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
            {checking ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {checking ? "Checking…" : "Check Connection"}
          </button>
          {canCheck && conn.base_url_configured && (
            <button onClick={() => setShowLogin(true)}
              data-testid="redington-signin-btn"
              className="inline-flex items-center gap-2 text-[12px] px-3.5 py-2 rounded-md bg-blue-600 text-white hover:bg-blue-700">
              <LogIn className="h-3.5 w-3.5" /> {isConnected ? "Re-sign in to Redington" : "Sign in to Redington"}
            </button>
          )}
          {!canCheck && (
            <span className="text-[11px] text-slate-400">Requires connector write access.</span>
          )}
        </div>
      </div>

      {/* Portal Endpoint Config (admins) */}
      {canAdmin && (
        <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="redington-config-card">
          <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
            <Settings className="h-4 w-4 text-slate-500" /> Portal endpoint
          </div>
          <p className="mt-1.5 text-[12px] text-slate-500">
            Non-secret base URL of the Redington portal (e.g. https://redingtonconnect.com).
            Used by Check Connection and the secure sign-in.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input value={baseInput} onChange={(e) => setBaseInput(e.target.value)}
              data-testid="redington-base-input" placeholder="https://redingtonconnect.com"
              className="flex-1 min-w-[240px] text-[12px] px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-400" />
            <button onClick={saveBase} disabled={savingBase || !baseInput.trim()}
              data-testid="redington-base-save"
              className="inline-flex items-center gap-2 text-[12px] px-3.5 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
              {savingBase ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Save
            </button>
          </div>
        </div>
      )}

      {/* DEP Device Lookup — only when connected */}
      {isConnected && canCheck && <RedingtonLookup />}

      {/* Setup instructions */}
      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="redington-setup-instructions">
        <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
          <HelpCircle className="h-4 w-4 text-slate-500" /> How secure sign-in works
        </div>
        <p className="mt-2 text-[12px] text-slate-500 leading-relaxed">
          An authorised operator signs in through a protected, server-side session:
          the Redington login page runs inside our backend and is streamed to you as
          images. You personally type the username, password and Security Code — we
          never fill or bypass them. On success we keep only an encrypted session
          reference, backend-only, with an expiry.
        </p>
        <ol className="mt-3 space-y-2" data-testid="redington-requirements-list">
          {(conn.setup_requirements || []).map((r, i) => (
            <li key={i} className="flex gap-2.5 text-[12px] text-slate-600">
              <span className="mt-0.5 h-4 w-4 shrink-0 rounded-full bg-slate-900 text-white grid place-items-center text-[9px]">{i + 1}</span>
              <span className="leading-relaxed">{r}</span>
            </li>
          ))}
        </ol>
        <div className="mt-4 rounded-lg bg-amber-50 ring-1 ring-amber-200 px-3 py-2.5 text-[11.5px] text-amber-800 leading-relaxed">
          Credentials and cookies stay backend-only — never entered in chat, never
          stored in the app, logs, or responses. A browser login in another tab does
          not authenticate our backend. <span className="font-medium">Connected</span>
          {" "}appears only after a real read-only check reaches an authenticated
          Redington page (a login page returning HTTP 200 is reported as
          Authentication required).
        </div>
        <div className="mt-3 text-[11px] text-slate-400" data-testid="redington-statuses-legend">
          Possible statuses: {(conn.statuses || []).join(" · ")}
        </div>
      </div>

      {showLogin && (
        <RedingtonLoginModal
          onClose={() => setShowLogin(false)}
          onDone={(c) => { if (c) setConn(c); setShowLogin(false); load(); }}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Secure interactive login modal (frames polled over authenticated /api).
// --------------------------------------------------------------------------- //
export function RedingtonLoginModal({ onClose, onDone, portal = "dep" }) {
  // stage: starting -> waiting (start ok, awaiting first image) -> ready | error
  const [stage, setStage] = useState("starting");
  const [frame, setFrame] = useState(null);
  const [phase, setPhase] = useState("LOGIN");
  const [finalizing, setFinalizing] = useState(false);
  const [notYet, setNotYet] = useState(false);
  const [notYetMsg, setNotYetMsg] = useState("");
  const autoFinalizedRef = useRef(false); // finalize once automatically when the portal shows an authenticated page
  const finalizeRef = useRef(null);
  const finalizeBusyRef = useRef(false);
  const [err, setErr] = useState("");
  const [attempt, setAttempt] = useState(0);
  const [creds, setCreds] = useState({ username: "", password: "", security: "" });
  const [detected, setDetected] = useState(null);     // {username,password,security,submit,labels}
  const [delivering, setDelivering] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deliverMsg, setDeliverMsg] = useState("");
  const imgRef = useRef(null);
  const stageEl = useRef(null);
  const readyRef = useRef(false);      // true once the first image is shown
  const startPromiseRef = useRef(null); // shared across StrictMode double-invoke
  const fetchRef = useRef(null);       // latest doFetch, for input handlers
  const failRef = useRef(0);
  const sidRef = useRef(null);         // session_id owned by THIS modal instance
  const seqRef = useRef(0);            // last rendered frame seq (drop stale ones)
  const pollingRef = useRef(false);    // guard against overlapping frame polls
  const inputChainRef = useRef(Promise.resolve()); // serialises input ordering

  const sidQS = () => (sidRef.current ? `?sid=${encodeURIComponent(sidRef.current)}` : "");

  useEffect(() => {
    let cancelled = false;
    let iv = null;
    let timer = null;
    readyRef.current = false;
    failRef.current = 0;
    seqRef.current = 0;

    const applyFrame = (data) => {
      // Drop out-of-order/stale responses so a slow older frame can't replace a
      // newer image already on screen.
      const seq = data.seq || 0;
      if (seq && seq < seqRef.current) return;
      seqRef.current = seq;
      setFrame(data.image);
      setPhase(data.phase);
      if (data.phase === "AUTHENTICATED") {
        setNotYet(false);
        if (!autoFinalizedRef.current) { autoFinalizedRef.current = true; setTimeout(() => finalizeRef.current?.(), 400); }
      }
      readyRef.current = true;
      setStage("ready");
      // Keep keyboard focus on the secure stage across frame refreshes so
      // typing never falls through to the page behind. Only reclaim focus when
      // it was lost to the body (never steal it from a control the user chose).
      const el = stageEl.current;
      if (el && (document.activeElement === document.body || document.activeElement === null)) {
        requestAnimationFrame(() => { try { el.focus({ preventScroll: true }); } catch { /* noop */ } });
      }
    };

    const doFetch = async () => {
      if (cancelled || pollingRef.current) return;
      pollingRef.current = true;
      try {
        const { data } = await api.get(
          "/apple-services/redington/session/frame" + sidQS());
        if (cancelled) return;
        failRef.current = 0;
        applyFrame(data);
        if (timer) { clearTimeout(timer); timer = null; }
      } catch (e) {
        if (cancelled) return;
        const code = e.response?.status;
        if (code === 410) {
          // Session ended intentionally (expired / superseded / host gone).
          setErr(e.response?.data?.detail ||
            "The secure session has ended. Please close and sign in again.");
          setStage("error");
          if (iv) clearInterval(iv);
        } else if (readyRef.current && (code === 404 || code === 409)) {
          failRef.current += 1;
          if (failRef.current >= 3) {
            setErr("The secure session has ended. Please close and sign in again.");
            setStage("error");
            if (iv) clearInterval(iv);
          }
        }
        // before first image: keep waiting; the bounded timeout guards us.
      } finally {
        pollingRef.current = false;
      }
    };
    fetchRef.current = doFetch;

    (async () => {
      try {
        // Both StrictMode effect runs await the SAME start (one browser launch).
        if (!startPromiseRef.current) {
          startPromiseRef.current = api.post("/apple-services/redington/session/start", null, { params: { portal } });
        }
        const { data: startData } = await startPromiseRef.current;
        if (cancelled) return;
        sidRef.current = startData?.session_id || null;
        // start returns the first login frame — render it immediately so there
        // is never a gap where start succeeded but no image appeared.
        if (startData?.image) {
          applyFrame(startData);
        } else {
          setStage("waiting");
          timer = setTimeout(() => {
            if (cancelled || readyRef.current) return;
            setErr("Timed out waiting for the Redington login image. Please try again.");
            setStage("error");
            if (iv) clearInterval(iv);
          }, 20000);
          await doFetch();
        }
        iv = setInterval(doFetch, 1200);
      } catch (e) {
        if (cancelled) return;
        startPromiseRef.current = null;   // allow a clean retry
        const d = e.response?.data?.detail;
        const diag = d?.diagnostics;
        setErr(typeof d === "string" ? d : d?.message ? `${d.message}${diag ? ` [${diag.origin === "upstream" ? "Redington portal" : diag.origin === "network" ? "network" : "app backend"} · stage ${diag.stage}: ${diag.error}]` : ""}` : "Could not open the secure login session. Please try again.");
        setStage("error");
      }
    })();

    return () => {
      cancelled = true;
      if (iv) clearInterval(iv);
      if (timer) clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attempt]);

  const retry = () => {
    startPromiseRef.current = null;
    readyRef.current = false;
    failRef.current = 0;
    sidRef.current = null;
    seqRef.current = 0;
    setErr(""); setFrame(null); setNotYet(false);
    setStage("starting");
    setAttempt((a) => a + 1);
  };

  const closeAndCancel = () => {
    setCreds({ username: "", password: "", security: "" });
    api.post("/apple-services/redington/session/cancel" + sidQS()).catch(() => {});
    onClose();
  };

  const sendInput = (payload) => {
    if (!readyRef.current) return;
    // Chain inputs so keystrokes/clicks are delivered strictly in order, then
    // refresh a frame once the input lands.
    inputChainRef.current = inputChainRef.current
      .then(async () => {
        try {
          await api.post("/apple-services/redington/session/input" + sidQS(), payload);
          await fetchRef.current?.();
        } catch { /* transient */ }
      })
      .catch(() => {});
    return inputChainRef.current;
  };

  const onImgClick = (e) => {
    e.stopPropagation();
    const el = imgRef.current; if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    sendInput({ action: "click", x: Math.max(0, Math.min(1, x)), y: Math.max(0, Math.min(1, y)) });
    stageEl.current?.focus({ preventScroll: true });
  };

  const onKeyDown = (e) => {
    const special = ["Backspace", "Enter", "Tab", "Delete", "ArrowLeft", "ArrowRight",
                     "ArrowUp", "ArrowDown", "Home", "End"];
    // Trap keystrokes INSIDE the secure frame. The stage is a div[tabindex], so
    // global window shortcuts (Create "c", new-quote "q", "g" nav, "?" help,
    // command palette) would otherwise fire on the operator's keystrokes, open
    // an overlay and steal focus to the top search bar — the demonstrated root
    // cause of the "layer on top" that hijacked typed credentials.
    e.preventDefault();
    e.stopPropagation();
    e.nativeEvent?.stopImmediatePropagation?.();
    if (e.key === "Escape") return;
    if (e.key.length === 1) sendInput({ action: "type", text: e.key });
    else if (special.includes(e.key)) sendInput({ action: "key", key: e.key });
  };

  const onWheel = (e) => {
    // Forward scrolling so the operator can reach the login form below the fold.
    e.preventDefault();
    sendInput({ action: "scroll", dy: e.deltaY > 0 ? 0.35 : -0.35 });
  };

  // Attach wheel as a non-passive native listener so preventDefault actually
  // works (React's onWheel is passive) and the page behind doesn't scroll.
  useEffect(() => {
    const el = stageEl.current;
    if (stage !== "ready" || !el) return undefined;
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  // Detect the portal's real login fields once the first frame is up, so the
  // modal can offer reliable labelled inputs (coordinate-typing is unreliable).
  const detectFields = async () => {
    try {
      const { data } = await api.get("/apple-services/redington/session/detect" + sidQS());
      setDetected(data.fields || null);
    } catch { /* keep manual typing available */ }
  };
  useEffect(() => {
    if (stage === "ready" && detected === null) detectFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const deliver = async () => {
    const body = {};
    ["username", "password", "security"].forEach((k) => { if (creds[k]) body[k] = creds[k]; });
    if (!Object.keys(body).length) { toast.warning("Type a username, password or Security Code first."); return; }
    setDelivering(true); setDeliverMsg("");
    try {
      const { data } = await api.post("/apple-services/redington/session/fill" + sidQS(), body);
      await fetchRef.current?.();
      const nf = (data.not_found || []).filter((r) => body[r] !== undefined);
      setDeliverMsg(nf.length ? `Delivered ${data.delivered.join(", ") || "none"}; could not place: ${nf.join(", ")}.` : `Delivered to portal: ${data.delivered.join(", ")}.`);
      toast.success("Sent to the Redington login page");
    } catch (e) {
      const code = e.response?.status;
      if (code === 410) { setErr(e.response?.data?.detail || "The secure session has ended."); setStage("error"); }
      else toast.error(e.response?.data?.detail || "Could not deliver the fields.");
    } finally { setDelivering(false); }
  };

  const signIn = async () => {
    setSubmitting(true); setNotYet(false);
    try {
      const { data } = await api.post("/apple-services/redington/session/submit" + sidQS());
      setTimeout(() => fetchRef.current?.(), 900);
      const outcome = data.outcome;
      if (outcome === "authenticated") {
        // Submit reached an authenticated page — confirm with the real read-only
        // check before anything is shown as Connected.
        setCreds({ username: "", password: "", security: "" });
        toast.success("Signed in — verifying the connection…");
        await finalize();
      } else if (outcome === "portal_error") {
        setDeliverMsg(data.detail ? `Redington: ${data.detail}` : "Redington rejected the sign-in. Check the username, password and Security Code, then try again.");
        toast.error(data.detail || "Sign-in was rejected by Redington.");
      } else if (outcome === "still_on_login") {
        setDeliverMsg("Still on the login page — the Security Code may be wrong or expired. Re-read the code above and try again.");
        toast.warning("Still on the login page. Re-enter the Security Code and try again.");
      } else {
        setDeliverMsg("No response from Redington yet. Wait a moment and use \"I've completed sign-in\" to re-check.");
        toast.warning("No clear response yet — re-check after a moment.");
      }
    } catch (e) {
      const code = e.response?.status;
      if (code === 410) { setErr(e.response?.data?.detail || "The secure session has ended."); setStage("error"); }
      else setDeliverMsg(e.response?.data?.detail || "Could not submit the login form.") || toast.error(e.response?.data?.detail || "Could not submit the login form.");
    } finally { setSubmitting(false); }
  };

  const finalize = async () => {
    if (finalizeBusyRef.current) return;
    finalizeBusyRef.current = true;
    setFinalizing(true); setNotYet(false); setNotYetMsg("");
    try {
      const { data } = await api.post("/apple-services/redington/session/finalize" + sidQS());
      if (data.authenticated) {
        setCreds({ username: "", password: "", security: "" });
        const f = data.follow_up;
        if (portal === "applecare" && data.history_available === false) {
          toast.warning(data.message || "Signed in, but Order History is currently unavailable upstream.");
        } else {
          toast.success(portal === "applecare"
            ? `AppleCare connector session established — API verified (${data.api_check?.rows ?? "?"} rows)${f?.action === "resumed" ? " · paused sync resumed from its checkpoint" : f?.action === "sync" ? " · incremental sync started" : f?.action === "pipeline" ? " · import pipeline started" : ""}${data.page_warning ? ` · note: ${data.page_warning}` : ""}`
            : "Redington connection established");
        }
        onDone(data.connection);
      } else {
        setNotYet(true);
        setNotYetMsg(data.phase && data.phase !== "LOGIN" ? data.message : "");
        toast.warning(data.message || "Not signed in yet — complete the login and Security Code first.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not verify sign-in");
    } finally { setFinalizing(false); finalizeBusyRef.current = false; }
  };
  finalizeRef.current = finalize;

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="redington-login-modal">
      <div className="bg-white rounded-xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <MonitorSmartphone className="h-4 w-4 text-blue-600" />
            <span className="text-[13px] font-semibold text-slate-800">Secure Redington sign-in</span>
            <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ring-1 ${phase === "AUTHENTICATED" ? "bg-emerald-50 text-emerald-700 ring-emerald-200" : "bg-amber-50 text-amber-700 ring-amber-200"}`} data-testid="redington-login-phase">
              {phase === "AUTHENTICATED" ? "Authenticated" : "Login page"}
            </span>
          </div>
          <button onClick={closeAndCancel} data-testid="redington-login-close" className="text-slate-400 hover:text-slate-700"><X className="h-4 w-4" /></button>
        </div>

        <div className="px-5 py-2 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-100">
          Scroll to the login form, click a field, then type on your keyboard —
          including the Security Code. Your keystrokes go to the portal running
          securely on our backend. We never see or store your password.
        </div>
        {notYet && (
          <div className={`px-5 py-2 text-[11.5px] border-b ${notYetMsg ? "text-rose-800 bg-rose-50 border-rose-200" : "text-amber-800 bg-amber-50 border-amber-200"}`} data-testid="redington-login-notyet">
            {notYetMsg || (phase === "AUTHENTICATED"
              ? "The portal page is open, but the connector session is not confirmed yet — verifying…"
              : "Not signed in yet — complete the username, password and Security Code in the frame above, then click \"I've completed sign-in\" again.")}
          </div>
        )}

        <div className="flex-1 overflow-auto p-4 grid place-items-center bg-slate-100">
          {stage === "error" ? (
            <div className="py-16 flex flex-col items-center gap-3 max-w-md text-center" data-testid="redington-login-error">
              <div className="text-rose-600 text-[13px]">{err}</div>
              <div className="flex items-center gap-2">
                <button onClick={retry} data-testid="redington-login-retry"
                  className="text-[12px] px-3.5 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800">Try again</button>
                <button onClick={closeAndCancel}
                  className="text-[12px] px-3.5 py-2 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Close</button>
              </div>
            </div>
          ) : stage !== "ready" ? (
            <div className="py-20 text-slate-400 flex flex-col items-center gap-2" data-testid="redington-login-starting">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="text-[12px]">{stage === "waiting" ? "Loading the Redington login page…" : "Opening secure session…"}</span>
              <span className="text-[11px] text-slate-400">Launching a private browser and loading the Redington login page — this can take up to ~10 seconds.</span>
            </div>
          ) : (
            <div ref={stageEl} tabIndex={0} onKeyDown={onKeyDown}
              className="outline-none focus:ring-2 focus:ring-blue-400 rounded-md"
              data-testid="redington-login-stage">
              {frame ? (
                <img ref={imgRef} src={frame} alt="Redington login" onClick={onImgClick}
                  data-testid="redington-login-frame"
                  className="max-w-full rounded-md shadow-sm cursor-crosshair select-none" draggable={false} />
              ) : (
                <div className="py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
              )}
            </div>
          )}
        </div>

        {stage === "ready" && (
          <div className="px-5 py-3 border-t border-slate-200 bg-slate-50/70" data-testid="redington-cred-panel" data-dd-privacy="mask">
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11.5px] font-semibold text-slate-700">Type your credentials here — delivered securely to the portal above</span>
              {detected && (
                <span className="text-[10px] text-slate-500" data-testid="redington-cred-detect">
                  {detected.username || detected.password
                    ? `Fields found: ${[detected.username && "username", detected.password && "password", detected.security && "Security Code"].filter(Boolean).join(" · ")}`
                    : "Login fields not detected — click a field in the image and type instead."}
                </span>
              )}
            </div>
            <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2">
              <label className="text-[11px] text-slate-600">{detected?.labels?.username || "Username"}
                <input value={creds.username} onChange={(e) => setCreds((c) => ({ ...c, username: e.target.value }))}
                  autoComplete="off" spellCheck={false} data-testid="redington-cred-username" data-dd-privacy="mask" data-private="true"
                  className="mt-0.5 w-full text-[12px] px-2.5 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </label>
              <label className="text-[11px] text-slate-600">Password
                <input type="password" value={creds.password} onChange={(e) => setCreds((c) => ({ ...c, password: e.target.value }))}
                  autoComplete="new-password" data-testid="redington-cred-password" data-dd-privacy="mask" data-private="true"
                  className="mt-0.5 w-full text-[12px] px-2.5 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </label>
              <label className="text-[11px] text-slate-600">{detected?.labels?.security || "Security Code (CAPTCHA)"}
                <input value={creds.security} onChange={(e) => setCreds((c) => ({ ...c, security: e.target.value }))}
                  autoComplete="off" spellCheck={false} data-testid="redington-cred-security" data-dd-privacy="mask" data-private="true"
                  placeholder="read from the image above" className="mt-0.5 w-full text-[12px] px-2.5 py-1.5 rounded-md border border-slate-300 focus:outline-none focus:ring-1 focus:ring-blue-400" />
              </label>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <button onClick={deliver} disabled={delivering} data-testid="redington-cred-deliver"
                className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
                {delivering ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <LogIn className="h-3.5 w-3.5" />} Send to portal
              </button>
              <button onClick={signIn} disabled={submitting} data-testid="redington-cred-signin"
                className="inline-flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
                {submitting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />} Sign in
              </button>
              <span className="text-[10.5px] text-slate-500">The Security Code is entered by you and never solved automatically. Nothing you type here is stored or logged.</span>
              {deliverMsg && <span className="text-[10.5px] text-slate-600" data-testid="redington-cred-msg">{deliverMsg}</span>}
            </div>
          </div>
        )}
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-200">
          <span className="text-[11px] text-slate-400">Type credentials below, Send to portal, then Sign in — or click a field in the image and type directly.</span>
          <div className="flex items-center gap-2">
            <button onClick={closeAndCancel} data-testid="redington-login-cancel"
              className="text-[12px] px-3.5 py-2 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50">Cancel</button>
            <button onClick={finalize} disabled={finalizing || stage !== "ready"}
              data-testid="redington-login-finalize"
              className="inline-flex items-center gap-2 text-[12px] px-3.5 py-2 rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
              {finalizing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              I've completed sign-in
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// DEP device lookup (read-only)
// --------------------------------------------------------------------------- //
const LK_TOOLS = [
  { id: "distributor", label: "Distributor order", ep: "distributor-order",
    fields: [{ k: "order_number", ph: "Distributor order number (e.g. 5002845163)" }] },
  { id: "dep", label: "DEP order", ep: "dep-order",
    fields: [{ k: "order_no", ph: "DEP order number" }, { k: "reseller_id", ph: "Reseller ID (optional)", opt: true }] },
  { id: "status", label: "Device status", ep: "device-status",
    fields: [{ k: "order_no", ph: "DEP order number" }] },
  { id: "history", label: "Device history", ep: "device-history",
    fields: [{ k: "device", ph: "Device serial" }] },
];

const OUTCOME_TONE = {
  ENROLLED_COMPLETE: "bg-emerald-50 text-emerald-700",
  RETURNED_COMPLETE: "bg-sky-50 text-sky-700",
  COMPLETE_UNSPECIFIED: "bg-amber-50 text-amber-700",
  PENDING: "bg-amber-50 text-amber-700",
  IN_PROGRESS: "bg-blue-50 text-blue-700",
  UNKNOWN: "bg-slate-100 text-slate-500",
  OTHER: "bg-slate-100 text-slate-600",
};
const SRC_TONE = {
  OK: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  EMPTY: "bg-slate-100 text-slate-500 ring-slate-200",
  AUTH_REQUIRED: "bg-amber-50 text-amber-700 ring-amber-200",
  SCHEMA_CHANGED: "bg-orange-50 text-orange-700 ring-orange-200",
  ERROR: "bg-rose-50 text-rose-700 ring-rose-200",
};

function OutcomeBadge({ row }) {
  const o = row.derived_outcome;
  if (!o) return null;
  return (
    <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] ${OUTCOME_TONE[o] || OUTCOME_TONE.OTHER}`} title={row.raw_source_status || ""}>
      {o.replaceAll("_", " ").toLowerCase()}
    </span>
  );
}

function DeviceTable({ rows }) {
  if (!rows?.length) return null;
  return (
    <div className="overflow-x-auto rounded-lg border border-slate-100 mt-2">
      <table className="min-w-full text-[11px]">
        <thead className="bg-slate-50 text-slate-500">
          <tr>
            <th className="px-2.5 py-1.5 text-left">Device ID</th>
            <th className="px-2.5 py-1.5 text-left">Asset Tag</th>
            <th className="px-2.5 py-1.5 text-left">Source status</th>
            <th className="px-2.5 py-1.5 text-left">Outcome</th>
            <th className="px-2.5 py-1.5 text-left">Order No</th>
            <th className="px-2.5 py-1.5 text-left">Type</th>
            <th className="px-2.5 py-1.5 text-left">Date</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-t border-slate-50" data-testid={`redington-device-row-${i}`}>
              <td className="px-2.5 py-1.5 font-mono text-slate-700">{r["Device ID"] || "—"}</td>
              <td className="px-2.5 py-1.5 text-slate-700">{r["Asset Tag"] || "—"}</td>
              <td className="px-2.5 py-1.5 text-slate-700">{(r.raw_source_status || "").trim() || <span className="text-slate-400 italic">unknown</span>}</td>
              <td className="px-2.5 py-1.5"><OutcomeBadge row={r} /></td>
              <td className="px-2.5 py-1.5 font-mono text-slate-600">{r["Order Number"] || r.dep_order_number || "—"}</td>
              <td className="px-2.5 py-1.5 text-slate-600">{r["Order Type"] || r.order_type || "—"}</td>
              <td className="px-2.5 py-1.5 text-slate-600">{r["Date"] || "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RedingtonLookup() {
  const [tool, setTool] = useState("distributor");
  const [vals, setVals] = useState({});
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const active = LK_TOOLS.find((t) => t.id === tool);

  const setVal = (k, v) => setVals((s) => ({ ...s, [k]: v }));

  const search = async () => {
    const body = {};
    for (const f of active.fields) {
      const v = (vals[f.k] || "").trim();
      if (!v && !f.opt) { toast.error(`Enter ${f.ph}`); return; }
      if (v) body[f.k] = v;
    }
    setBusy(true); setRes(null);
    try {
      const { data } = await api.post(`/apple-services/redington/lookup/${active.ep}`, body);
      setRes(data);
      if (data.source_status === "AUTH_REQUIRED") toast.warning("Redington session expired — please sign in again.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Lookup failed");
    } finally { setBusy(false); }
  };

  const fmt = (iso) => { if (!iso) return "—"; try { return new Date(iso).toLocaleString(); } catch { return iso; } };
  const distRows = res?.kind === "distributor_order" ? res.rows : null;
  const statusRows = (res?.kind === "device_status" || res?.kind === "device_history") ? res.rows : null;

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="redington-lookup-card">
      <div className="flex items-center gap-2 text-[13px] font-semibold text-slate-800">
        <ScanSearch className="h-4 w-4 text-slate-500" /> DEP order &amp; device lookup
        <span className="text-[10px] font-normal text-slate-400">(read-only preview)</span>
      </div>

      {/* Tool selector */}
      <div className="mt-3 flex flex-wrap gap-1.5">
        {LK_TOOLS.map((t) => (
          <button key={t.id} onClick={() => { setTool(t.id); setRes(null); setVals({}); }}
            data-testid={`redington-tool-${t.id}`}
            className={"text-[11px] px-2.5 py-1.5 rounded-md border " +
              (tool === t.id ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300")}>
            {t.label}
          </button>
        ))}
      </div>

      {/* Inputs */}
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {active.fields.map((f) => (
          <input key={f.k} value={vals[f.k] || ""} onChange={(e) => setVal(f.k, e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && search()}
            data-testid={`redington-input-${f.k}`} placeholder={f.ph}
            className="flex-1 min-w-[200px] text-[12px] px-3 py-2 rounded-md border border-slate-200 focus:outline-none focus:ring-1 focus:ring-slate-400" />
        ))}
        <button onClick={search} disabled={busy}
          data-testid="redington-lookup-btn"
          className="inline-flex items-center gap-2 text-[12px] px-3.5 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-50">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />} Search
        </button>
      </div>
      <div className="mt-2 text-[10.5px] text-slate-400">
        Read-only preview — no enrolment or return is submitted and no records are created or updated.
      </div>

      {res && (
        <div className="mt-4" data-testid="redington-lookup-result">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] ring-1 ${SRC_TONE[res.source_status] || "bg-slate-100 text-slate-600 ring-slate-200"}`} data-testid="redington-source-status">
              {res.source_status}
            </span>
            {res.stale && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-800" data-testid="redington-stale-badge">STALE</span>
            )}
            <span className="text-[10.5px] text-slate-400">Last checked: {fmt(res.last_checked)}</span>
          </div>
          {res.note && <div className="text-[11.5px] text-slate-500 italic mb-2">{res.note}</div>}

          {/* Distributor order rows */}
          {distRows && distRows.length > 0 && (
            <div className="overflow-x-auto rounded-lg border border-slate-100">
              <table className="min-w-full text-[11px]">
                <thead className="bg-slate-50 text-slate-500"><tr>
                  <th className="px-2.5 py-1.5 text-left">Model</th>
                  <th className="px-2.5 py-1.5 text-left">Serial</th>
                  <th className="px-2.5 py-1.5 text-left">Distributor order</th>
                </tr></thead>
                <tbody>
                  {distRows.map((r, i) => (
                    <tr key={i} className="border-t border-slate-50" data-testid={`redington-dist-row-${i}`}>
                      <td className="px-2.5 py-1.5 text-slate-700">{r.model || "—"}</td>
                      <td className="px-2.5 py-1.5 font-mono text-slate-700">{r.serial || "—"}</td>
                      <td className="px-2.5 py-1.5 font-mono text-slate-500">{r.distributor_order_number}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Device status / history rows */}
          {statusRows && <DeviceTable rows={statusRows} />}

          {/* DEP order summary + detail */}
          {res.kind === "dep_order" && (
            <div className="space-y-3">
              {res.rows?.length > 0 && (
                <div className="overflow-x-auto rounded-lg border border-slate-100">
                  <table className="min-w-full text-[11px]">
                    <thead className="bg-slate-50 text-slate-500"><tr>
                      {["Order Number", "Order Date", "Order Type", "Customer ID", "Customer PO Number", "Partner ID/Name", "End Customer", "Transaction ID"].map((c) => (
                        <th key={c} className="px-2.5 py-1.5 text-left whitespace-nowrap">{c}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {res.rows.map((r, i) => (
                        <tr key={i} className="border-t border-slate-50" data-testid={`redington-dep-row-${i}`}>
                          {["Order Number", "Order Date", "Order Type", "Customer ID", "Customer PO Number", "Partner ID/Name", "End Customer", "Transaction ID"].map((c) => (
                            <td key={c} className="px-2.5 py-1.5 text-slate-700 whitespace-nowrap">{r[c] || "—"}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {res.detail?.devices?.length > 0 && (
                <div>
                  <div className="text-[11px] font-medium text-slate-600">
                    Devices for order {res.detail.selected_order_no}
                    {res.detail.transaction_id ? ` · Txn ${res.detail.transaction_id}` : ""}
                    {res.detail.order_type ? ` · Type ${res.detail.order_type}` : ""}
                  </div>
                  <DeviceTable rows={res.detail.devices} />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// ---------------------------------------------------------------------------
function OverviewTab({ overview }) {
  if (!overview) return <div className="text-[11px] text-slate-400 p-4">Loading…</div>;
  const enrollExc = overview.enroll_exceptions || 0;
  const careExc = overview.care_exceptions || 0;
  const cards = [
    { label: "Customer Profiles",   v: overview.profiles,          icon: Users,          tone: "slate" },
    { label: "Enrollments Pending", v: overview.enrollments?.pending || 0, icon: Clock,   tone: "amber" },
    { label: "Completed Today",     v: overview.completed_today,   icon: CheckCircle2,   tone: "emerald" },
    { label: "AppleCare Requested", v: overview.applecare?.requested || 0, icon: Clock,   tone: "amber" },
    { label: "AppleCare Activated Today", v: overview.activated_today, icon: ShieldCheck,tone: "emerald" },
    { label: "Enrollment Exceptions", v: enrollExc, icon: AlertTriangle, tone: enrollExc > 0 ? "rose" : "slate" },
    { label: "AppleCare Exceptions",  v: careExc,   icon: AlertTriangle, tone: careExc > 0 ? "rose" : "slate" },
  ];
  const toneMap = {
    slate:   "bg-slate-50 text-slate-700",
    amber:   "bg-amber-50 text-amber-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose:    "bg-rose-50 text-rose-700",
  };
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="apple-svc-overview">
      {cards.map((c) => {
        const Icon = c.icon;
        return (
          <div key={c.label} className={`rounded-lg p-3 ring-1 ring-slate-200 ${toneMap[c.tone]}`}
                data-testid={`apple-svc-stat-${c.label.toLowerCase().replaceAll(" ", "-")}`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-wide opacity-70">{c.label}</div>
              <Icon className="h-3.5 w-3.5 opacity-70" />
            </div>
            <div className="text-2xl font-heading font-semibold mt-1">{c.v ?? 0}</div>
          </div>
        );
      })}
    </div>
  );
}


// ---------------------------------------------------------------------------
// Profiles
// ---------------------------------------------------------------------------
function ProfilesTab({ profiles, refresh, meta }) {
  const [dlg, setDlg] = useState(false);
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <div className="text-[13px] font-heading font-semibold">Customer Apple Profiles</div>
        <div className="text-[10px] text-slate-500">{profiles.length} profile(s)</div>
        <div className="flex-1" />
        {meta?.current_user?.can_write && (
          <button onClick={() => setDlg(true)}
                data-testid="apple-profile-new"
                className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> New Profile
          </button>
        )}
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-[11.5px]" data-testid="apple-profiles-table">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Apple Org ID</th>
              <th className="text-left px-3 py-2">Country</th>
              <th className="text-left px-3 py-2">Contact</th>
              <th className="text-left px-3 py-2">DEP</th>
              <th className="text-left px-3 py-2">AppleCare</th>
              <th className="text-left px-3 py-2">Reports</th>
            </tr>
          </thead>
          <tbody>
            {profiles.length === 0 && (
              <tr><td colSpan={7} className="text-[11px] text-slate-400 px-3 py-6 text-center">
                No profiles yet. Create one — or use the Seed Demo Data action.
              </td></tr>
            )}
            {profiles.map((p) => (
              <tr key={p.id} className="border-t border-slate-100 hover:bg-slate-50"
                    data-testid={`apple-profile-row-${p.id}`}>
                <td className="px-3 py-2 font-medium">{p.customer_name || p.customer_id}</td>
                <td className="px-3 py-2 font-mono text-[11px]">{p.apple_organization_id}</td>
                <td className="px-3 py-2">{p.country || "—"}</td>
                <td className="px-3 py-2">{p.apple_contact_email || p.apple_contact_name || "—"}</td>
                <td className="px-3 py-2">{p.dep_active ? "Active" : "—"}</td>
                <td className="px-3 py-2">{p.applecare_active ? "Active" : "—"}</td>
                <td className="px-3 py-2">{p.combined_report ? "Combined" : "Separate"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dlg && <ProfileDialog onClose={() => setDlg(false)} onSaved={() => { setDlg(false); refresh(); }} />}
    </div>
  );
}


function ProfileDialog({ onClose, onSaved }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({
    customer_id: "", customer_name: "", apple_organization_id: "",
    country: "", legal_entity: "",
    apple_contact_name: "", apple_contact_email: "",
    account_owner: "", dep_active: true, applecare_active: true,
    combined_report: false, notes: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/master-clients?limit=200").then(({ data }) => setClients(data.items || []));
  }, []);

  const save = async () => {
    if (!form.customer_id || !form.apple_organization_id) {
      toast.error("Customer and Apple Organization ID are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/apple-services/profiles", form);
      toast.success("Profile created");
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || d || "Failed to create profile");
    } finally { setBusy(false); }
  };

  const pick = (id) => {
    const c = clients.find((x) => x.id === id);
    setForm((f) => ({ ...f, customer_id: id,
                      customer_name: c?.name,
                      legal_entity: c?.legal_name,
                      country: c?.primary_country }));
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4"
          data-testid="apple-profile-dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl p-4 space-y-3">
        <div className="text-[13px] font-heading font-semibold">New Customer Apple Profile</div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-[10px] text-slate-500">
            Customer
            <select value={form.customer_id} onChange={(e) => pick(e.target.value)}
                data-testid="apple-profile-customer"
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
              <option value="">— select —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          <label className="text-[10px] text-slate-500">
            Apple Organization ID
            <input value={form.apple_organization_id}
                data-testid="apple-profile-orgid"
                onChange={(e) => setForm({ ...form, apple_organization_id: e.target.value })}
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5 font-mono" />
          </label>
          <label className="text-[10px] text-slate-500">
            Apple Contact Name
            <input value={form.apple_contact_name}
                onChange={(e) => setForm({ ...form, apple_contact_name: e.target.value })}
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          </label>
          <label className="text-[10px] text-slate-500">
            Apple Contact Email
            <input value={form.apple_contact_email}
                data-testid="apple-profile-email"
                onChange={(e) => setForm({ ...form, apple_contact_email: e.target.value })}
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          </label>
          <label className="text-[10px] text-slate-500">
            Account Owner
            <input value={form.account_owner}
                onChange={(e) => setForm({ ...form, account_owner: e.target.value })}
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          </label>
          <label className="text-[10px] text-slate-500">
            Report Style
            <select value={form.combined_report ? "combined" : "separate"}
                onChange={(e) => setForm({ ...form, combined_report: e.target.value === "combined" })}
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
              <option value="separate">Separate reports</option>
              <option value="combined">Combined Apple Services report</option>
            </select>
          </label>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={form.dep_active}
                onChange={(e) => setForm({ ...form, dep_active: e.target.checked })} />
            DEP Active
          </label>
          <label className="flex items-center gap-1.5">
            <input type="checkbox" checked={form.applecare_active}
                onChange={(e) => setForm({ ...form, applecare_active: e.target.checked })} />
            AppleCare Active
          </label>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">
            Cancel
          </button>
          <button onClick={save} disabled={busy}
              data-testid="apple-profile-save"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Create Profile"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Enrollments
// ---------------------------------------------------------------------------
function EnrollmentsTab({ enrollments, profiles, refresh, meta }) {
  const [dlg, setDlg] = useState(false);
  const [bulk, setBulk] = useState(false);
  const [filter, setFilter] = useState("");
  const filtered = useMemo(() => {
    const q = filter.toLowerCase();
    if (!q) return enrollments;
    return enrollments.filter((e) =>
      [e.serial_number, e.apple_organization_id, e.model, e.product]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q)));
  }, [enrollments, filter]);

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[13px] font-heading font-semibold">Automated Device Enrollment (DEP)</div>
        <div className="text-[10px] text-slate-500">{filtered.length} record(s)</div>
        <div className="flex-1" />
        <div className="relative">
          <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={filter} onChange={(e) => setFilter(e.target.value)}
              placeholder="Search serial, org, model"
              data-testid="apple-enroll-search"
              className="text-[11px] border border-slate-200 rounded-md pl-7 pr-2 py-1.5 w-64" />
        </div>
        {meta?.current_user?.can_write && (
          <>
            <button onClick={() => setBulk(true)}
                data-testid="apple-enroll-bulk"
                className="text-[11px] px-2.5 py-1.5 rounded-md border border-slate-300 flex items-center gap-1.5">
              <Upload className="h-3.5 w-3.5" /> Bulk Import
            </button>
            <button onClick={() => setDlg(true)}
                data-testid="apple-enroll-new"
                className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white flex items-center gap-1.5">
              <Plus className="h-3.5 w-3.5" /> New
            </button>
          </>
        )}
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-[11.5px]" data-testid="apple-enroll-table">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Record ID</th>
              <th className="text-left px-3 py-2">Serial</th>
              <th className="text-left px-3 py-2">Model</th>
              <th className="text-left px-3 py-2">Apple Org</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2">Requested</th>
              <th className="text-left px-3 py-2">Completed</th>
              <th className="text-left px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="text-[11px] text-slate-400 px-3 py-6 text-center">
                No enrollment records. Seed demo data or create one.
              </td></tr>
            )}
            {filtered.map((e) => (
              <EnrollmentRow key={e.id} e={e} refresh={refresh} canWrite={meta?.current_user?.can_write} />
            ))}
          </tbody>
        </table>
      </div>
      {dlg && <EnrollmentDialog profiles={profiles}
                                 onClose={() => setDlg(false)}
                                 onSaved={() => { setDlg(false); refresh(); }} />}
      {bulk && <BulkEnrollmentDialog profiles={profiles}
                                       onClose={() => setBulk(false)}
                                       onSaved={(opts) => {
                                         refresh();
                                         if (!opts?.keepOpen) setBulk(false);
                                       }} />}
    </div>
  );
}


function EnrollmentRow({ e, refresh, canWrite }) {
  const complete = async () => {
    try {
      await api.patch(`/apple-services/enrollments/${e.id}`, { status: "COMPLETED" });
      toast.success("Marked completed");
      refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || d || "Failed");
    }
  };
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50"
          data-testid={`apple-enroll-row-${e.id}`}>
      <td className="px-3 py-2 font-mono text-[10.5px]">{e.enrollment_record_id}</td>
      <td className="px-3 py-2 font-mono">{e.serial_number}</td>
      <td className="px-3 py-2">{e.model || e.product || "—"}</td>
      <td className="px-3 py-2 font-mono text-[10.5px]">{e.apple_organization_id}</td>
      <td className="px-3 py-2">{badge(ENROLL_BADGE, e.status)}</td>
      <td className="px-3 py-2 text-[10.5px]">{(e.enrollment_requested_date || e.created_at || "").slice(0, 10)}</td>
      <td className="px-3 py-2 text-[10.5px]">{(e.enrollment_completed_date || "").slice(0, 10) || "—"}</td>
      <td className="px-3 py-2 text-right">
        {canWrite && e.status !== "COMPLETED" && (
          <button onClick={complete}
              data-testid={`apple-enroll-complete-${e.id}`}
              className="text-[10.5px] px-2 py-1 rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50">
            Complete
          </button>
        )}
      </td>
    </tr>
  );
}


function EnrollmentDialog({ profiles, onClose, onSaved }) {
  const [profileId, setProfileId] = useState("");
  const [form, setForm] = useState({
    customer_id: "", apple_organization_id: "", serial_number: "",
    product: "", model: "", part_number: "",
  });
  const [busy, setBusy] = useState(false);

  const pickProfile = (id) => {
    setProfileId(id);
    const p = profiles.find((x) => x.id === id);
    if (p) setForm((f) => ({ ...f, customer_id: p.customer_id,
                                apple_organization_id: p.apple_organization_id }));
  };
  const save = async () => {
    if (!form.customer_id || !form.apple_organization_id || !form.serial_number) {
      toast.error("Customer, Org ID and Serial are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/apple-services/enrollments", form);
      toast.success("Enrollment created");
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || d || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-4 space-y-3"
            data-testid="apple-enroll-dialog">
        <div className="text-[13px] font-heading font-semibold">New Enrollment</div>
        <label className="text-[10px] text-slate-500 block">
          Customer Apple Profile
          <select value={profileId} onChange={(e) => pickProfile(e.target.value)}
              data-testid="apple-enroll-profile"
              className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
            <option value="">— select —</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.customer_name} · {p.apple_organization_id}
              </option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Serial Number" value={form.serial_number}
              testid="apple-enroll-serial"
              onChange={(v) => setForm({ ...form, serial_number: v })} mono />
          <Field label="Part Number" value={form.part_number}
              onChange={(v) => setForm({ ...form, part_number: v })} />
          <Field label="Product" value={form.product}
              onChange={(v) => setForm({ ...form, product: v })} />
          <Field label="Model" value={form.model}
              onChange={(v) => setForm({ ...form, model: v })} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={save} disabled={busy}
              data-testid="apple-enroll-save"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}


function BulkEnrollmentDialog({ profiles, onClose, onSaved }) {
  const [profileId, setProfileId] = useState("");
  const [serialText, setSerialText] = useState("");
  const [product, setProduct] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    const p = profiles.find((x) => x.id === profileId);
    if (!p) { toast.error("Pick a profile"); return; }
    const serials = serialText.split(/[\s,;\n\r]+/).map((s) => s.trim()).filter(Boolean);
    if (!serials.length) { toast.error("Paste at least one serial"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/apple-services/enrollments/bulk", {
        customer_id: p.customer_id,
        apple_organization_id: p.apple_organization_id,
        legal_entity: p.legal_entity,
        product, model, serials,
      });
      setResult(data);
      toast.success(`Created ${data.created_count} · Duplicates ${data.duplicate_count}`);
      // Refresh parent list without closing so the user can see the summary.
      onSaved && onSaved({ keepOpen: true, result: data });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-4 space-y-3"
            data-testid="apple-enroll-bulk-dialog">
        <div className="text-[13px] font-heading font-semibold">Bulk Enrollment Import</div>
        <label className="text-[10px] text-slate-500 block">
          Customer Apple Profile
          <select value={profileId} onChange={(e) => setProfileId(e.target.value)}
              data-testid="apple-bulk-profile"
              className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
            <option value="">— select —</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>{p.customer_name} · {p.apple_organization_id}</option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Product (all)" value={product} onChange={setProduct} />
          <Field label="Model (all)" value={model} onChange={setModel} />
        </div>
        <label className="text-[10px] text-slate-500 block">
          Serials (paste — space, comma, or newline separated)
          <textarea value={serialText} onChange={(e) => setSerialText(e.target.value)}
              data-testid="apple-bulk-serials"
              rows={5}
              className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5 font-mono" />
        </label>
        {result && (
          <div className="text-[11px] rounded-md bg-slate-50 p-2 space-y-1">
            <div><b>Created:</b> {result.created_count}</div>
            <div><b>Duplicates skipped:</b> {result.duplicate_count}</div>
            {result.duplicates?.length > 0 && (
              <div className="text-[10px] text-amber-700">
                {result.duplicates.map((d) => d.serial_number).join(", ")}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Close</button>
          <button onClick={submit} disabled={busy}
              data-testid="apple-bulk-submit"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// AppleCare
// ---------------------------------------------------------------------------
function AppleCareTab({ applecare, profiles, refresh, meta, goTo }) {
  const [dlg, setDlg] = useState(false);
  const demoCount = applecare.filter((r) => r.demo_seed || /^TEST/i.test(r.serial_number || "")).length;
  return (
    <div className="space-y-3">
      <AppleCareIntegrationCard goTo={goTo} />
      <DistributorActivationPanel />
      <div className="flex items-center gap-2">
        <div className="text-[13px] font-heading font-semibold">AppleCare+ Register <span className="text-[10px] font-normal text-slate-500">(local CRM register — not distributor data; “New AppleCare” never contacts Redington — use the distributor activation panel above for a real activation)</span></div>
        <div className="text-[10px] text-slate-500" data-testid="apple-care-register-count">{applecare.length} record(s){demoCount ? ` · ${demoCount} TEST/DEMO` : ""}</div>
        <div className="flex-1" />
        {meta?.current_user?.can_write && (
          <button onClick={() => setDlg(true)}
              data-testid="apple-care-new"
              className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white flex items-center gap-1.5">
            <Plus className="h-3.5 w-3.5" /> New AppleCare
          </button>
        )}
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-[11.5px]" data-testid="apple-care-table">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Record ID</th>
              <th className="text-left px-3 py-2">Serial</th>
              <th className="text-left px-3 py-2">Plan</th>
              <th className="text-left px-3 py-2">Coverage Start</th>
              <th className="text-left px-3 py-2">Coverage End</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-left px-3 py-2 w-24"></th>
            </tr>
          </thead>
          <tbody>
            {applecare.length === 0 && (
              <tr><td colSpan={7} className="text-[11px] text-slate-400 px-3 py-6 text-center">
                No AppleCare records yet.
              </td></tr>
            )}
            {applecare.map((c) => (
              <AppleCareRow key={c.id} c={c} refresh={refresh} canWrite={meta?.current_user?.can_write} />
            ))}
          </tbody>
        </table>
      </div>
      {dlg && <AppleCareDialog profiles={profiles}
                                 onClose={() => setDlg(false)}
                                 onSaved={() => { setDlg(false); refresh(); }} />}
    </div>
  );
}


function AppleCareRow({ c, refresh, canWrite }) {
  const activate = async () => {
    try {
      await api.patch(`/apple-services/applecare/${c.id}`, { status: "ACTIVATED" });
      toast.success("Marked activated");
      refresh();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || d || "Failed");
    }
  };
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50"
          data-testid={`apple-care-row-${c.id}`}>
      <td className="px-3 py-2 font-mono text-[10.5px]">{c.applecare_record_id}{(c.demo_seed || /^TEST/i.test(c.serial_number || "")) && <span className="ml-1 px-1 rounded bg-amber-100 text-amber-800 font-sans text-[9px]" data-testid={`apple-care-demo-${c.applecare_record_id}`}>TEST/DEMO</span>}</td>
      <td className="px-3 py-2 font-mono">{c.serial_number}</td>
      <td className="px-3 py-2">{c.applecare_product || c.plan_type}</td>
      <td className="px-3 py-2 text-[10.5px]">{(c.coverage_start || "").slice(0, 10) || "—"}</td>
      <td className="px-3 py-2 text-[10.5px]">{(c.coverage_end || "").slice(0, 10) || "—"}</td>
      <td className="px-3 py-2">{badge(CARE_BADGE, c.status)}</td>
      <td className="px-3 py-2 text-right">
        {canWrite && c.status !== "ACTIVATED" && (
          <button onClick={activate}
              data-testid={`apple-care-activate-${c.id}`}
              className="text-[10.5px] px-2 py-1 rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50">
            Activate
          </button>
        )}
      </td>
    </tr>
  );
}


function AppleCareDialog({ profiles, onClose, onSaved }) {
  const [profileId, setProfileId] = useState("");
  const [form, setForm] = useState({
    customer_id: "", apple_organization_id: "", serial_number: "",
    plan_type: "APPLECARE+_FOR_BUSINESS", applecare_product: "",
    product: "", model: "",
  });
  const [busy, setBusy] = useState(false);
  const pickProfile = (id) => {
    setProfileId(id);
    const p = profiles.find((x) => x.id === id);
    if (p) setForm((f) => ({ ...f, customer_id: p.customer_id,
                                apple_organization_id: p.apple_organization_id }));
  };
  const save = async () => {
    if (!form.customer_id || !form.serial_number) {
      toast.error("Customer and Serial are required");
      return;
    }
    setBusy(true);
    try {
      await api.post("/apple-services/applecare", form);
      toast.success("AppleCare record created");
      onSaved();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || d || "Failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl p-4 space-y-3"
            data-testid="apple-care-dialog">
        <div className="text-[13px] font-heading font-semibold">New AppleCare Record</div>
        <label className="text-[10px] text-slate-500 block">
          Customer Apple Profile
          <select value={profileId} onChange={(e) => pickProfile(e.target.value)}
              data-testid="apple-care-profile"
              className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
            <option value="">— select —</option>
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>{p.customer_name} · {p.apple_organization_id}</option>
            ))}
          </select>
        </label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Serial Number" value={form.serial_number}
              testid="apple-care-serial"
              onChange={(v) => setForm({ ...form, serial_number: v })} mono />
          <label className="text-[10px] text-slate-500 block">
            Plan Type
            <select value={form.plan_type}
                onChange={(e) => setForm({ ...form, plan_type: e.target.value })}
                data-testid="apple-care-plantype"
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
              <option value="APPLECARE+">AppleCare+</option>
              <option value="APPLECARE+_FOR_BUSINESS">AppleCare+ for Business</option>
              <option value="APPLECARE_PROTECTION_PLAN">AppleCare Protection Plan</option>
              <option value="OTHER">Other</option>
            </select>
          </label>
          <Field label="AppleCare Product (label)" value={form.applecare_product}
              onChange={(v) => setForm({ ...form, applecare_product: v })} />
          <Field label="Product" value={form.product}
              onChange={(v) => setForm({ ...form, product: v })} />
          <Field label="Model" value={form.model}
              onChange={(v) => setForm({ ...form, model: v })} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
          <button onClick={save} disabled={busy}
              data-testid="apple-care-save"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            {busy ? "Saving…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Exceptions Tab (server-side status filter — accurate regardless of pagination)
// ---------------------------------------------------------------------------
function ExceptionsTab() {
  const [enrollExceptions, setEnrollExceptions] = useState([]);
  const [careExceptions, setCareExceptions]     = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/apple-services/enrollments", { params: { status: "FAILED", limit: 500 } }),
      api.get("/apple-services/enrollments", { params: { status: "REVIEW_REQUIRED", limit: 500 } }),
      api.get("/apple-services/applecare",   { params: { status: "ACTIVATION_FAILED", limit: 500 } }),
      api.get("/apple-services/applecare",   { params: { status: "REVIEW_REQUIRED", limit: 500 } }),
    ]).then(([f, r, af, cr]) => {
      setEnrollExceptions([...(f.data.items || []), ...(r.data.items || [])]);
      setCareExceptions([...(af.data.items || []), ...(cr.data.items || [])]);
    }).catch(() => {});
  }, []);

  return (
    <div className="space-y-4" data-testid="apple-svc-exceptions">
      <div>
        <div className="text-[12px] font-heading font-semibold mb-2 flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
          Enrollment Exceptions ({enrollExceptions.length})
        </div>
        {enrollExceptions.length === 0 ? (
          <div className="text-[11px] text-slate-400 border border-slate-200 rounded-lg p-4 bg-white">
            No enrollment exceptions.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full text-[11.5px]">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Serial</th>
                  <th className="text-left px-3 py-2">Apple Org</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {enrollExceptions.map((e) => (
                  <tr key={e.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono">{e.serial_number}</td>
                    <td className="px-3 py-2 font-mono text-[10.5px]">{e.apple_organization_id}</td>
                    <td className="px-3 py-2">{badge(ENROLL_BADGE, e.status)}</td>
                    <td className="px-3 py-2 text-[10.5px] text-slate-500">
                      {e.review_reason || e.notes || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div>
        <div className="text-[12px] font-heading font-semibold mb-2 flex items-center gap-1.5">
          <AlertTriangle className="h-3.5 w-3.5 text-rose-600" />
          AppleCare Exceptions ({careExceptions.length})
        </div>
        {careExceptions.length === 0 ? (
          <div className="text-[11px] text-slate-400 border border-slate-200 rounded-lg p-4 bg-white">
            No AppleCare exceptions.
          </div>
        ) : (
          <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
            <table className="w-full text-[11.5px]">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="text-left px-3 py-2">Serial</th>
                  <th className="text-left px-3 py-2">Plan</th>
                  <th className="text-left px-3 py-2">Status</th>
                  <th className="text-left px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {careExceptions.map((c) => (
                  <tr key={c.id} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-mono">{c.serial_number}</td>
                    <td className="px-3 py-2">{c.applecare_product || c.plan_type}</td>
                    <td className="px-3 py-2">{badge(CARE_BADGE, c.status)}</td>
                    <td className="px-3 py-2 text-[10.5px] text-slate-500">{c.notes || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function Field({ label, value, onChange, mono, testid }) {
  return (
    <label className="text-[10px] text-slate-500 block">
      {label}
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
          data-testid={testid}
          className={"mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5 " +
                      (mono ? "font-mono" : "")} />
    </label>
  );
}


function PlaceholderTab({ title, hint }) {
  return (
    <div className="border border-dashed border-slate-300 rounded-lg p-6 bg-slate-50/40 text-center"
          data-testid={`apple-svc-placeholder-${title.toLowerCase().replaceAll(" ", "-")}`}>
      <div className="text-[13px] font-heading font-semibold text-slate-700">{title}</div>
      <div className="text-[11px] text-slate-500 mt-1 max-w-md mx-auto">{hint}</div>
    </div>
  );
}



// ---------------------------------------------------------------------------
// Phase 2 — Daily Completions
// ---------------------------------------------------------------------------
const DELIVERY_BADGE = {
  SENT:          "bg-emerald-50 text-emerald-700 ring-emerald-200",
  MOCKED_LOGGED: "bg-slate-100 text-slate-600 ring-slate-200",
  FAILED:        "bg-rose-50 text-rose-700 ring-rose-200",
};

function DailyCompletionsTab({ meta }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate]   = useState(today);
  const [data, setData]   = useState(null);
  const [busy, setBusy]   = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/apple-services/daily/build", { params: { date } });
      setData(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to build daily digest"); }
  }, [date]);
  useEffect(() => { load(); }, [load]);

  const sendOne = async (row, force = false) => {
    setBusy(true);
    try {
      const { data: d } = await api.post("/apple-services/daily/send", {
        customer_id: row.customer_id, date, force_resend: force,
      });
      const st = d.communication?.delivery_status;
      if (st === "SENT" || st === "MOCKED_LOGGED") toast.success(`Sent to ${row.customer_name}`);
      else toast.error(`Delivery ${st} — see Communications for details`);
      await load();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail?.message || detail || "Send failed");
    } finally { setBusy(false); }
  };

  const rows = data?.rows || [];
  const eligible = rows.filter((r) => !r.already_sent && r.contact_email);

  const sendAll = async () => {
    if (!window.confirm(`Send daily completion emails to ${eligible.length} Customer(s)?`)) return;
    setBusy(true);
    try {
      const { data: d } = await api.post("/apple-services/daily/send-all", { date });
      toast.success(`Sent ${d.sent_count} · Skipped ${d.skipped_count} · Errors ${d.error_count}`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || "Send-all failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="apple-svc-daily">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[13px] font-heading font-semibold">Daily Completions</div>
        <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
            data-testid="apple-daily-date"
            className="text-[11px] border border-slate-200 rounded-md px-2 py-1.5" />
        <div className="text-[10px] text-slate-500">
          {data ? `${data.total_customers} customer(s) · ${data.total_devices} device(s)` : ""}
        </div>
        <div className="flex-1" />
        {meta?.current_user?.can_write && eligible.length > 0 && (
          <button onClick={sendAll} disabled={busy}
              data-testid="apple-daily-send-all"
              className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-50">
            Approve &amp; Send All ({eligible.length})
          </button>
        )}
      </div>

      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-[11.5px]" data-testid="apple-daily-table">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Org ID</th>
              <th className="text-left px-3 py-2">Devices</th>
              <th className="text-left px-3 py-2">Summary</th>
              <th className="text-left px-3 py-2">Recipient</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2 w-40"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={7} className="text-[11px] text-slate-400 px-3 py-6 text-center">
                No completions on {date}.
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.customer_id} className="border-t border-slate-100"
                    data-testid={`apple-daily-row-${r.customer_id}`}>
                <td className="px-3 py-2 font-medium">{r.customer_name}</td>
                <td className="px-3 py-2 font-mono text-[10.5px]">{r.apple_organization_id}</td>
                <td className="px-3 py-2 font-semibold">{r.devices_completed}</td>
                <td className="px-3 py-2 text-slate-500">{r.devices_summary}</td>
                <td className="px-3 py-2 text-[10.5px]">
                  {r.contact_email || <span className="text-rose-600">missing</span>}
                </td>
                <td className="px-3 py-2">
                  {r.already_sent
                    ? <span className="text-[10px] text-emerald-700">✓ Sent</span>
                    : <span className="text-[10px] text-amber-700">Ready</span>}
                </td>
                <td className="px-3 py-2 text-right space-x-1">
                  <button onClick={() => setPreview(r)}
                      data-testid={`apple-daily-preview-${r.customer_id}`}
                      className="text-[10.5px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">
                    Preview
                  </button>
                  {meta?.current_user?.can_write && !r.already_sent && r.contact_email && (
                    <button onClick={() => sendOne(r)} disabled={busy}
                        data-testid={`apple-daily-send-${r.customer_id}`}
                        className="text-[10.5px] px-2 py-1 rounded border border-emerald-500 text-emerald-700 hover:bg-emerald-50">
                      Send
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {preview && <PreviewDrawer row={preview} onClose={() => setPreview(null)}
                                   canWrite={meta?.current_user?.can_write}
                                   onSend={() => { sendOne(preview); setPreview(null); }} />}
    </div>
  );
}


function PreviewDrawer({ row, onClose, canWrite, onSend }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="apple-daily-preview-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-[520px] max-w-full h-full bg-white shadow-xl overflow-y-auto">
        <div className="p-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase text-slate-500">Email Preview</div>
            <div className="text-[13px] font-heading font-semibold">{row.customer_name}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              To: {row.contact_email || "—"} · {row.devices_completed} device(s)
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-[10px] uppercase text-slate-500">Subject</div>
            <div className="text-[12px] font-medium mt-1" data-testid="apple-preview-subject">{row.subject}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Body</div>
            <pre className="text-[11.5px] whitespace-pre-wrap font-sans mt-1 p-3 rounded-md bg-slate-50 border border-slate-200"
                  data-testid="apple-preview-body">{row.body}</pre>
          </div>
          {canWrite && !row.already_sent && row.contact_email && (
            <button onClick={onSend}
                data-testid="apple-preview-send"
                className="w-full text-[11.5px] px-3 py-2 rounded-md bg-slate-900 text-white hover:bg-slate-800">
              Send Now
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 2 — Communications history
// ---------------------------------------------------------------------------
function CommunicationsTab({ profiles }) {
  const [items, setItems]   = useState([]);
  const [filter, setFilter] = useState({ customer_id: "", delivery_status: "" });
  const [drawer, setDrawer] = useState(null);

  const load = useCallback(async () => {
    const params = {};
    if (filter.customer_id) params.customer_id = filter.customer_id;
    if (filter.delivery_status) params.delivery_status = filter.delivery_status;
    try {
      const { data } = await api.get("/apple-services/communications", { params });
      setItems(data.items || []);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load history"); }
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="apple-svc-comms">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[13px] font-heading font-semibold">Communications History</div>
        <select value={filter.customer_id}
            onChange={(e) => setFilter({ ...filter, customer_id: e.target.value })}
            data-testid="apple-comms-filter-customer"
            className="text-[11px] border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">All Customers</option>
          {profiles.map((p) => <option key={p.customer_id} value={p.customer_id}>{p.customer_name}</option>)}
        </select>
        <select value={filter.delivery_status}
            onChange={(e) => setFilter({ ...filter, delivery_status: e.target.value })}
            data-testid="apple-comms-filter-status"
            className="text-[11px] border border-slate-200 rounded-md px-2 py-1.5">
          <option value="">All Statuses</option>
          <option value="SENT">Sent</option>
          <option value="MOCKED_LOGGED">Mocked (dev)</option>
          <option value="FAILED">Failed</option>
        </select>
        <div className="text-[10px] text-slate-500">{items.length} record(s)</div>
      </div>
      <div className="border border-slate-200 rounded-lg overflow-hidden bg-white">
        <table className="w-full text-[11.5px]" data-testid="apple-comms-table">
          <thead className="bg-slate-50 text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Sent At</th>
              <th className="text-left px-3 py-2">Customer</th>
              <th className="text-left px-3 py-2">Kind</th>
              <th className="text-left px-3 py-2">Recipient</th>
              <th className="text-left px-3 py-2">Subject</th>
              <th className="text-left px-3 py-2">Status</th>
              <th className="text-right px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="text-[11px] text-slate-400 px-3 py-6 text-center">
                No communications yet — send a daily email to populate.
              </td></tr>
            )}
            {items.map((c) => (
              <tr key={c.id} className="border-t border-slate-100"
                    data-testid={`apple-comms-row-${c.id}`}>
                <td className="px-3 py-2 text-[10.5px]">{(c.sent_at || "").replace("T", " ").slice(0, 16)}</td>
                <td className="px-3 py-2">{c.customer_name}</td>
                <td className="px-3 py-2 text-[10.5px]">{c.kind}</td>
                <td className="px-3 py-2 text-[10.5px]">{c.recipient}</td>
                <td className="px-3 py-2 text-[10.5px] text-slate-500 truncate max-w-[240px]">{c.subject}</td>
                <td className="px-3 py-2">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] ring-1 ${DELIVERY_BADGE[c.delivery_status] || "bg-slate-100 text-slate-600 ring-slate-200"}`}>
                    {c.delivery_status}
                  </span>
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setDrawer(c)}
                      data-testid={`apple-comms-view-${c.id}`}
                      className="text-[10.5px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-50">
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {drawer && <CommunicationDrawer c={drawer} onClose={() => setDrawer(null)} />}
    </div>
  );
}


function CommunicationDrawer({ c, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="apple-comms-drawer">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-[560px] max-w-full h-full bg-white shadow-xl overflow-y-auto">
        <div className="p-4 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase text-slate-500">Sent Email</div>
            <div className="text-[13px] font-heading font-semibold">{c.customer_name}</div>
            <div className="text-[10px] text-slate-500 mt-0.5">
              {(c.sent_at || "").replace("T", " ").slice(0, 16)} · template v{c.template_version}
            </div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <Meta label="Recipient" value={c.recipient} />
          {c.cc?.length > 0 && <Meta label="CC" value={c.cc.join(", ")} />}
          <Meta label="Delivery" value={c.delivery_status + (c.provider ? ` · ${c.provider}` : "")} />
          {c.error && <Meta label="Error" value={c.error} tone="rose" />}
          <div>
            <div className="text-[10px] uppercase text-slate-500">Subject</div>
            <div className="text-[12px] font-medium mt-1">{c.subject}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Body</div>
            <pre className="text-[11.5px] whitespace-pre-wrap font-sans mt-1 p-3 rounded-md bg-slate-50 border border-slate-200">{c.body}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}


function Meta({ label, value, tone }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-slate-500">{label}</div>
      <div className={"text-[11.5px] mt-0.5 " + (tone === "rose" ? "text-rose-700" : "text-slate-700")}>{value}</div>
    </div>
  );
}


// ---------------------------------------------------------------------------
// Phase 2 — Configuration (daily email + template editor)
// ---------------------------------------------------------------------------
const MERGE_TOKENS = [
  "customer_name", "contact_name", "org_id", "date",
  "devices_completed", "devices_summary", "ytd_total",
  "account_manager_name", "account_manager_email",
];

function ConfigurationTab({ meta }) {
  const [config, setConfig]     = useState(null);
  const [cfgDirty, setCfgDirty] = useState(false);
  const [tmpl, setTmpl]         = useState(null);
  const [dirty, setDirty]       = useState(false);
  const [busy, setBusy]         = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [cfg, t] = await Promise.all([
        api.get("/apple-services/config"),
        api.get("/apple-services/communications/templates/daily_completion"),
      ]);
      setConfig(cfg.data.config);
      setCfgDirty(false);
      setTmpl(t.data.template);
      setDirty(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to load config"); }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  const patchCfg = (updater) => {
    setConfig((c) => ({ ...c, daily_email: { ...c.daily_email, ...updater } }));
    setCfgDirty(true);
  };

  const saveConfig = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/apple-services/config/daily-email", config.daily_email);
      setConfig(data.config);
      setCfgDirty(false);
      toast.success("Configuration saved");
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const saveTemplate = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/apple-services/communications/templates/daily_completion", {
        subject: tmpl.subject, body: tmpl.body,
      });
      setTmpl(data.template);
      setDirty(false);
      toast.success(data.unchanged ? "No changes to save" : `Template saved (v${data.template.version})`);
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const revertTo = async (version) => {
    if (!window.confirm(`Revert to template v${version}?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/apple-services/communications/templates/daily_completion/revert",
                                        { version });
      setTmpl(data.template);
      toast.success(`Reverted — now on v${data.template.version}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Revert failed"); }
    finally { setBusy(false); }
  };

  const insertToken = (field, token) => {
    setTmpl((t) => ({ ...t, [field]: `${t[field] || ""}{{${token}}}` }));
    setDirty(true);
  };

  if (!config || !tmpl) return <div className="text-[11px] text-slate-400 p-4">Loading…</div>;
  const canAdmin = meta?.current_user?.can_admin;
  const de = config.daily_email || {};

  return (
    <div className="space-y-6" data-testid="apple-svc-config">
      {/* Daily Email settings */}
      <div className="border border-slate-200 rounded-lg bg-white p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="text-[13px] font-heading font-semibold">Daily Completion Email</div>
          {!canAdmin && <span className="text-[10px] text-slate-500">(read-only — requires super_admin)</span>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <label className="text-[10px] text-slate-500 block">
            Enabled
            <select disabled={!canAdmin} value={de.enabled ? "yes" : "no"}
                data-testid="apple-cfg-enabled"
                onChange={(e) => patchCfg({ enabled: e.target.value === "yes" })}
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
              <option value="yes">Yes</option><option value="no">No</option>
            </select>
          </label>
          <label className="text-[10px] text-slate-500 block">
            Mode
            <select disabled={!canAdmin} value={de.mode}
                data-testid="apple-cfg-mode"
                onChange={(e) => patchCfg({ mode: e.target.value })}
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
              <option value="PREPARE_FOR_REVIEW">Prepare for Review</option>
              <option value="AUTO_SEND">Auto Send</option>
              <option value="DISABLED">Disabled</option>
            </select>
          </label>
          <label className="text-[10px] text-slate-500 block">
            Send Time (local)
            <input disabled={!canAdmin} type="time" value={de.send_time_local || "17:30"}
                data-testid="apple-cfg-time"
                onChange={(e) => patchCfg({ send_time_local: e.target.value })}
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          </label>
          <label className="text-[10px] text-slate-500 block">
            Timezone
            <input disabled={!canAdmin} value={de.timezone || "Asia/Kolkata"}
                data-testid="apple-cfg-timezone"
                onChange={(e) => patchCfg({ timezone: e.target.value })}
                className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
          </label>
        </div>
        <div className="flex items-center gap-4 text-[11px]">
          <label className="flex items-center gap-1.5">
            <input type="checkbox" disabled={!canAdmin} checked={!!de.cc_account_owner}
                data-testid="apple-cfg-cc-owner"
                onChange={(e) => patchCfg({ cc_account_owner: e.target.checked })} />
            CC internal Account Owner
          </label>
        </div>
        {canAdmin && (
          <div className="flex justify-end">
            <button onClick={saveConfig} disabled={busy || !cfgDirty}
                data-testid="apple-cfg-save"
                className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40">
              {busy ? "Saving…" : cfgDirty ? "Save Configuration" : "No changes"}
            </button>
          </div>
        )}
      </div>

      {/* Template Editor */}
      <div className="border border-slate-200 rounded-lg bg-white p-4 space-y-3">
        <div className="flex items-center gap-2">
          <div className="text-[13px] font-heading font-semibold">Daily Completion Template</div>
          <div className="text-[10px] text-slate-500">v{tmpl.version} · {(tmpl.updated_at || "").slice(0, 10)}</div>
          <div className="flex-1" />
          {tmpl.versions?.length > 1 && (
            <select disabled={!canAdmin} defaultValue=""
                data-testid="apple-cfg-revert"
                onChange={(e) => { if (e.target.value) revertTo(parseInt(e.target.value)); e.target.value = ""; }}
                className="text-[10.5px] border border-slate-200 rounded-md px-2 py-1">
              <option value="">Revert to…</option>
              {tmpl.versions.slice(0, -1).map((v) => (
                <option key={v.version} value={v.version}>v{v.version} · {(v.at || "").slice(0, 10)}</option>
              ))}
            </select>
          )}
        </div>
        <div className="flex flex-wrap gap-1">
          {MERGE_TOKENS.map((t) => (
            <button key={t} disabled={!canAdmin}
                onClick={() => insertToken("body", t)}
                data-testid={`apple-cfg-token-${t}`}
                className="text-[10px] px-2 py-0.5 rounded-full border border-slate-300 hover:bg-slate-100 disabled:opacity-40">
              {"{{"}{t}{"}}"}
            </button>
          ))}
        </div>
        <label className="text-[10px] text-slate-500 block">
          Subject
          <input disabled={!canAdmin} value={tmpl.subject}
              data-testid="apple-cfg-tmpl-subject"
              onChange={(e) => { setTmpl({ ...tmpl, subject: e.target.value }); setDirty(true); }}
              className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        </label>
        <label className="text-[10px] text-slate-500 block">
          Body
          <textarea disabled={!canAdmin} value={tmpl.body} rows={12}
              data-testid="apple-cfg-tmpl-body"
              onChange={(e) => { setTmpl({ ...tmpl, body: e.target.value }); setDirty(true); }}
              className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5 font-mono" />
        </label>
        {canAdmin && (
          <div className="flex justify-end">
            <button onClick={saveTemplate} disabled={busy || !dirty}
                data-testid="apple-cfg-tmpl-save"
                className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40">
              {busy ? "Saving…" : dirty ? "Save Template" : "No changes"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
